import { useTranslation } from 'react-i18next';
import { Skeleton } from '@/components/ui/skeleton';

type Variant = 'default' | 'table' | 'form' | 'list';

export function RouteSkeleton({ variant = 'default' }: { variant?: Variant }) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-4 p-4 sm:p-6">
      {/* Accessibility: announce the loading state to screen readers */}
      <span
        role="status"
        aria-live="polite"
        className="sr-only"
      >
        {t('common.loading.page')}
      </span>

      {variant === 'table' && <TableVariant />}
      {variant === 'form' && <FormVariant />}
      {variant === 'list' && <ListVariant />}
      {variant === 'default' && <DefaultVariant />}
    </div>
  );
}

function DefaultVariant() {
  return (
    <>
      {/* Header row skeleton */}
      <div className="flex items-center justify-between gap-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-9 w-28" />
      </div>

      {/* Content rows skeleton */}
      <div className="flex flex-col gap-3">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full sm:w-4/5" />
        <Skeleton className="h-12 w-full sm:w-3/5" />
      </div>
    </>
  );
}

function TableVariant() {
  return (
    <>
      {/* Header + action button row */}
      <div className="flex items-center justify-between gap-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-9 w-28" />
      </div>

      {/* Filter bar row */}
      <div className="flex items-center gap-3">
        <Skeleton className="h-9 flex-1 max-w-xs" />
        <Skeleton className="h-9 w-28" />
        <Skeleton className="h-9 w-24" />
      </div>

      {/* Table header */}
      <div className="flex items-center gap-3 border-b border-border pb-2">
        <Skeleton className="size-4" />
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-4 flex-1" />
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-4 w-20" />
      </div>

      {/* Table rows */}
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 py-1">
          <Skeleton className="size-4" />
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-5 flex-1" />
          <Skeleton className="h-5 w-24" />
          <Skeleton className="h-5 w-20" />
        </div>
      ))}
    </>
  );
}

function FormVariant() {
  return (
    <>
      {/* Header */}
      <div className="flex items-center gap-3">
        <Skeleton className="size-8" />
        <Skeleton className="h-8 w-48" />
      </div>

      {/* Two-column field grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5 mt-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="flex flex-col gap-1.5">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-9 w-full" />
          </div>
        ))}
      </div>

      {/* Sticky-footer bar placeholder */}
      <div className="flex items-center justify-end gap-3 mt-4 pt-4 border-t border-border">
        <Skeleton className="h-9 w-24" />
        <Skeleton className="h-9 w-28" />
      </div>
    </>
  );
}

function ListVariant() {
  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-9 w-28" />
      </div>

      {/* Cards */}
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 py-2">
          <Skeleton className="size-10 rounded-full shrink-0" />
          <div className="flex flex-col gap-1.5 flex-1 min-w-0">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-3 w-60 max-w-full" />
          </div>
        </div>
      ))}
    </>
  );
}

RouteSkeleton.Table = function RouteSkeletonTable() {
  return <RouteSkeleton variant="table" />;
};

RouteSkeleton.Form = function RouteSkeletonForm() {
  return <RouteSkeleton variant="form" />;
};

RouteSkeleton.List = function RouteSkeletonList() {
  return <RouteSkeleton variant="list" />;
};

export default RouteSkeleton;
